/**
 * Browser Storage Utilities
 *
 * Provides consistent interfaces for cookies, session storage, and local storage
 * with proper typing, expiry handling, and inactivity detection.
 *
 * Storage Strategy:
 * - Cookies: Long-term (1 year), NEVER cleared except on logout
 * - Session Storage: Per-session with 60-min inactivity timeout
 * - Local Storage: Temporary, non-critical data
 */

// Debug logging toggle - set to false to reduce console noise
const DEBUG = false
const log = DEBUG ? console.log : () => {}

// ============================================================================
// CONSTANTS
// ============================================================================

const INACTIVITY_TIMEOUT = 60 * 60 * 1000 // 60 minutes in milliseconds
const LAST_ACTIVITY_KEY = 'lina_last_activity'

// ============================================================================
// COOKIE OPERATIONS (Long-term, never cleared by app)
// ============================================================================

/**
 * Set a cookie with long-term expiry
 * Used for: auth tokens, tour flags, user preferences
 */
export function setCookie(name: string, value: string, days: number = 365): void {
  const expiryDate = new Date()
  expiryDate.setDate(expiryDate.getDate() + days)

  const hostname = window.location.hostname
  const protocol = window.location.protocol

  // Dev environments: localhost, 127.0.0.1, GitHub Codespaces, Gitpod, etc.
  const isDevEnvironment =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.github.dev') ||
    hostname.includes('.gitpod.io') ||
    hostname.includes('.codespaces') ||
    hostname.includes('localhost.run') ||
    hostname.includes('.preview.app')

  // Production domain detection: only set domain for simple production domains (e.g., .example.com)
  // For dev environments or complex subdomains, omit domain to use exact hostname
  const isSimpleProductionDomain =
    !isDevEnvironment && hostname.split('.').length === 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/) // Not an IP

  const domain = isSimpleProductionDomain ? `; domain=.${hostname}` : ''

  // Use Secure flag for HTTPS (including Codespaces), but not for HTTP localhost
  const secure = protocol === 'https:' ? '; Secure' : ''

  const cookieString = `${name}=${value}; expires=${expiryDate.toUTCString()}; path=/${domain}; SameSite=Lax${secure}`

  if (import.meta.env.DEV) {
    console.log('[storage] Setting cookie:', {
      name,
      hostname,
      protocol,
      isDevEnvironment,
      isSimpleProductionDomain,
      domain: domain || '(none - exact hostname)',
      secure: secure || '(none - HTTP)',
      cookiePreview: cookieString.substring(0, 120) + '...',
    })
  }

  document.cookie = cookieString

  // Verify cookie was set
  if (import.meta.env.DEV) {
    const wasSet = document.cookie.includes(`${name}=`)
    if (!wasSet) {
      console.error('[storage] ⚠️ Cookie was NOT set! Possible causes:')
      console.error('  - Browser blocking cookies (check settings)')
      console.error('  - Incognito/Private mode')
      console.error('  - Third-party cookie restrictions')
      // Do not log document.cookie (may contain tokens)
    } else {
      console.log('[storage] ✓ Cookie set successfully:', name)
    }
  }
}

/**
 * Get a cookie value
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';')
  const cookie = cookies.find((c) => c.trim().startsWith(`${name}=`))

  if (!cookie) return null

  return cookie.split('=')[1] || null
}

/**
 * Delete a cookie (only use on explicit logout)
 */
export function deleteCookie(name: string): void {
  const hostname = window.location.hostname
  const isDevEnvironment =
    hostname === 'localhost' ||
    hostname === '127.0.0.1' ||
    hostname.includes('.github.dev') ||
    hostname.includes('.gitpod.io') ||
    hostname.includes('.codespaces') ||
    hostname.includes('localhost.run') ||
    hostname.includes('.preview.app')

  const isSimpleProductionDomain =
    !isDevEnvironment && hostname.split('.').length === 2 && !hostname.match(/^\d+\.\d+\.\d+\.\d+$/)

  const domain = isSimpleProductionDomain ? `; domain=.${hostname}` : ''

  document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 GMT; path=/${domain}`

  if (import.meta.env.DEV) {
    console.log('[storage] Deleted cookie:', name)
  }
}

// ============================================================================
// SESSION STORAGE (With Inactivity Timeout)
// ============================================================================

interface SessionData<T = any> {
  value: T
  timestamp: number
  timeoutMinutes: number
}

/**
 * Set session storage with inactivity timeout
 * Used for: analysis results, account cache, drafts
 */
export function setSession<T>(key: string, value: T, timeoutMinutes: number = 60): void {
  const data: SessionData<T> = {
    value,
    timestamp: Date.now(),
    timeoutMinutes,
  }

  sessionStorage.setItem(key, JSON.stringify(data))
  updateActivity()
}

/**
 * Get session storage value (returns null if expired)
 */
export function getSession<T>(key: string): T | null {
  const stored = sessionStorage.getItem(key)
  if (!stored) return null

  try {
    const data: SessionData<T> = JSON.parse(stored)
    const elapsed = Date.now() - data.timestamp
    const timeout = data.timeoutMinutes * 60 * 1000

    // Check if session data has expired
    if (elapsed > timeout) {
      sessionStorage.removeItem(key)
      return null
    }

    return data.value
  } catch (err) {
    console.error(`[storage] Error parsing session data for ${key}:`, err)
    sessionStorage.removeItem(key)
    return null
  }
}

/**
 * Update last activity timestamp
 * Called on user interactions (click, keydown, scroll)
 */
export function updateActivity(): void {
  sessionStorage.setItem(LAST_ACTIVITY_KEY, Date.now().toString())
}

/**
 * Check for inactivity and clear expired sessions
 * Called on app initialization
 */
export function clearExpiredSessions(): void {
  const lastActivity = sessionStorage.getItem(LAST_ACTIVITY_KEY)

  if (lastActivity) {
    const elapsed = Date.now() - parseInt(lastActivity)

    if (elapsed > INACTIVITY_TIMEOUT) {
      log('[storage] Inactivity timeout reached, clearing session storage')
      // Clear all session storage
      sessionStorage.clear()

      // Set new activity timestamp
      updateActivity()
    }
  } else {
    // First session, set activity timestamp
    updateActivity()
  }
}

/**
 * Get all session keys (for debugging)
 */
export function getSessionKeys(): string[] {
  const keys: string[] = []
  for (let i = 0; i < sessionStorage.length; i++) {
    const key = sessionStorage.key(i)
    if (key) keys.push(key)
  }
  return keys
}

// ============================================================================
// LOCAL STORAGE (Temporary, non-critical)
// ============================================================================

/**
 * Set temporary local storage (OK if lost)
 * Used for: debug flags, temporary UI state
 */
export function setTempStorage<T>(key: string, value: T): void {
  try {
    localStorage.setItem(key, JSON.stringify(value))
  } catch (err) {
    console.error(`[storage] Error setting temp storage for ${key}:`, err)
  }
}

/**
 * Get temporary local storage value
 */
export function getTempStorage<T>(key: string): T | null {
  try {
    const stored = localStorage.getItem(key)
    return stored ? JSON.parse(stored) : null
  } catch (err) {
    console.error(`[storage] Error getting temp storage for ${key}:`, err)
    return null
  }
}

/**
 * Clear all temporary local storage
 * Used sparingly, only for cleanup
 */
export function clearTempStorage(): void {
  localStorage.clear()
}

// ============================================================================
// MIGRATION HELPERS
// ============================================================================

/**
 * Migrate data from localStorage to cookies
 * Used during gradual migration
 */
export function migrateLocalStorageToCookie(
  localStorageKey: string,
  cookieName: string,
  days: number = 365,
): void {
  const value = localStorage.getItem(localStorageKey)
  if (value) {
    setCookie(cookieName, value, days)
    localStorage.removeItem(localStorageKey)
    log(`[storage] Migrated ${localStorageKey} to cookie ${cookieName}`)
  }
}

/**
 * Migrate data from localStorage to session storage
 */
export function migrateLocalStorageToSession<T>(
  localStorageKey: string,
  sessionKey: string,
  timeoutMinutes: number = 60,
): void {
  const value = localStorage.getItem(localStorageKey)
  if (value) {
    try {
      // Try to parse as JSON first
      let parsed: T
      try {
        parsed = JSON.parse(value) as T
      } catch {
        // If JSON parse fails, use the raw string value
        parsed = value as T
      }
      setSession(sessionKey, parsed, timeoutMinutes)
      localStorage.removeItem(localStorageKey)
      log(`[storage] Migrated ${localStorageKey} to session storage ${sessionKey}`)
    } catch (err) {
      console.error(`[storage] Error migrating ${localStorageKey}:`, err)
    }
  }
}

// ============================================================================
// LOGOUT HELPER
// ============================================================================

/**
 * Clear session data on logout
 * NEVER clears auth cookies - those persist until explicit logout
 */
export function clearSessionOnLogout(): void {
  log('[storage] Clearing session storage on logout')
  sessionStorage.clear()
}

/**
 * Full logout - clear session storage AND auth cookies
 * Only use when user explicitly logs out
 */
export function fullLogout(): void {
  log('[storage] Full logout - clearing session and auth cookies')

  localStorage.clear()

  // Clear session storage
  sessionStorage.clear()

  // Clear legacy auth cookie (but NOT tour/preference cookies)
  deleteCookie('lina_verification_token')
}

// ============================================================================
// INITIALIZATION
// ============================================================================

/**
 * Initialize storage system
 * Call on app load
 */
export function initializeStorage(): void {
  log('[storage] Initializing storage system')

  // Clear expired sessions
  clearExpiredSessions()

  // Set up activity listeners
  document.addEventListener('click', updateActivity)
  document.addEventListener('keydown', updateActivity)
  document.addEventListener('scroll', updateActivity)

  // Migrate legacy data (run once)
  migrateLegacyStorage()
}

/**
 * Migrate legacy localStorage items to proper storage
 */
function migrateLegacyStorage(): void {
  // Check if migration already run
  const migrationDone = getCookie('storage_migration_v1')
  if (migrationDone) return

  log('[storage] Running legacy storage migration')

  // Migrate tour flags to cookies
  const toursShown = localStorage.getItem('lina_tours_shown')
  if (toursShown) {
    setCookie('lina_tours_shown', toursShown, 3650) // 10 years - effectively permanent
    localStorage.removeItem('lina_tours_shown')
  }

  // Migrate fingerprint to cookie
  const fingerprint = localStorage.getItem('lina_device_fingerprint')
  if (fingerprint) {
    setCookie('lina_device_fingerprint', fingerprint, 3650) // 10 years - effectively permanent
    localStorage.removeItem('lina_device_fingerprint')
  }

  // Migrate account cache to session storage
  migrateLocalStorageToSession('lina-account-cache', 'lina-account-cache', 60)
  migrateLocalStorageToSession('lina-language', 'lina-language', 60)
  migrateLocalStorageToSession('lina-theme', 'lina-theme', 60)

  // Mark migration as complete
  setCookie('storage_migration_v1', 'true', 365)
  log('[storage] Legacy storage migration complete')
}

// ============================================================================
// TYPE EXPORTS
// ============================================================================

export type { SessionData }
